import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Container,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Chip,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Logout as LogoutIcon,
  SportsBasketball as BasketballIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Player, SimilarPlayer } from '../types/Player';
import { searchPlayers, findSimilarPlayers, Gender } from '../services/playerService';
import { useAuth } from '../contexts/AuthContext';

const ZoneAILogo: React.FC<{ size?: number; color?: string }> = ({ size = 200, color = '#3D263B' }) => (
  <img 
    src="/zoneai-logo.svg" 
    alt="ZoneAI Logo" 
    style={{ 
      width: size, 
      height: size * 0.25,
      filter: color === '#ffffff' ? 'brightness(0) invert(1)' : 'none'
    }} 
  />
);

interface SearchScreenProps {
  onLogout: () => void;
}

export const SearchScreen: React.FC<SearchScreenProps> = ({ onLogout }) => {
  const [gender, setGender] = useState<Gender>('men');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Player[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [similarPlayers, setSimilarPlayers] = useState<SimilarPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const { user } = useAuth();

  useEffect(() => {
    if (searchQuery.length > 2) {
      const timeoutId = setTimeout(() => {
        searchPlayers(searchQuery, gender).then(setSuggestions);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, gender]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setShowSuggestions(query.length > 2);
    setSelectedSuggestionIndex(-1); // Reset selection when typing
  };

  const handleGenderChange = (event: React.SyntheticEvent, newGender: Gender) => {
    setGender(newGender);
    // Clear current search results when switching genders
    setSearchQuery('');
    setSuggestions([]);
    setSelectedPlayer(null);
    setSimilarPlayers([]);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          handleSelectPlayer(suggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleSelectPlayer = async (player: Player) => {
    setSelectedPlayer(player);
    setSearchQuery(player.name);
    setShowSuggestions(false);
    setLoading(true);

    try {
      const similar = await findSimilarPlayers(player, gender);
      setSimilarPlayers(similar);
    } catch (error) {
      console.error('Error finding similar players:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCardExpansion = (playerId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(playerId)) {
      newExpanded.delete(playerId);
    } else {
      newExpanded.add(playerId);
    }
    setExpandedCards(newExpanded);
  };

  const getSimilarityColor = (score: number) => {
    if (score >= 0.8) return '#2e7d32'; // Dark Green - Very Similar
    if (score >= 0.6) return '#4caf50'; // Medium Green - Similar
    return '#81c784'; // Light Green - Somewhat Similar
  };

  const getSimilarityLabel = (score: number) => {
    if (score >= 0.8) return 'Very Similar';
    if (score >= 0.6) return 'Similar';
    return 'Somewhat Similar';
  };

  const getClassDisplayName = (classCode: string) => {
    switch (classCode) {
      case 'FR': return 'Freshman';
      case 'SO': return 'Sophomore';
      case 'JR': return 'Junior';
      case 'SR': return 'Senior';
      default: return 'Senior';
    }
  };

  const renderPlayerCard = (similarPlayer: SimilarPlayer) => {
    const { player, similarityScore } = similarPlayer;
    const isExpanded = expandedCards.has(player.id);

    return (
      <Card key={player.id} sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(61, 38, 59, 0.08)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-2px)'
        }
      }}>
        <CardHeader
          sx={{
            '& .MuiCardHeader-content': {
              minWidth: 0, // Allow content to shrink
              flex: 1
            }
          }}
          avatar={
            <Avatar sx={{ 
              bgcolor: getSimilarityColor(similarityScore),
              width: 64,
              height: 64,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}>
              <ZoneAILogo size={40} color="#ffffff" />
            </Avatar>
          }
          title={
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#3D263B',
                cursor: 'pointer',
                '&:hover': {
                  color: '#E65E33',
                  textDecoration: 'underline'
                }
              }}
              onClick={() => handleSelectPlayer(player)}
            >
              {player.name}
            </Typography>
          }
          subheader={
            <Box sx={{ mt: 1 }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  mb: 1,
                  lineHeight: 1.4,
                  wordBreak: 'break-word'
                }}
              >
                {player.position} • {player.school} • 2024-25 • {getClassDisplayName(player.class)}
              </Typography>
              <Chip
                label={getSimilarityLabel(similarityScore)}
                size="small"
                sx={{
                  bgcolor: getSimilarityColor(similarityScore),
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  display: { xs: 'none', sm: 'inline-flex' } // Hide on mobile, show on desktop
                }}
              />
            </Box>
          }
          action={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              pt: 1
            }}>
              <Button
                onClick={() => toggleCardExpansion(player.id)}
                endIcon={isExpanded ? <ExpandLessIcon sx={{ fontSize: '1.5rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '1.5rem' }} />}
                sx={{ 
                  color: '#4caf50',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  py: 1,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    color: '#2e7d32',
                    bgcolor: 'rgba(76, 175, 80, 0.08)'
                  }
                }}
              >
                {isExpanded ? 'Less' : 'More'}
              </Button>
            </Box>
          }
        />
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ pt: 0, px: 4, pb: 4 }}>
            
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {player.stats.pointsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  PPG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {player.stats.reboundsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  RPG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {player.stats.assistsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  APG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {(player.stats.effectiveFieldGoalPercentage * 100).toFixed(1)}%
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  eFG%
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {player.stats.offensiveRating.toFixed(0)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Off Rating
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {player.stats.defensiveRating.toFixed(0)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Def Rating
                </Typography>
              </Box>
            </Box>
            
            {player.recruiting.committed && (
              <Box>
                <Chip
                  label={`Committed to ${player.recruiting.commitmentSchool}`}
                  sx={{
                    bgcolor: '#10B981',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    height: 32,
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                  }}
                />
              </Box>
            )}
            
            {player.recruiting.offers.length > 0 && (
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                >
                  <strong style={{ color: '#3D263B' }}>Offers:</strong> {player.recruiting.offers.slice(0, 3).join(', ')}
                  {player.recruiting.offers.length > 3 && '...'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#FAFAFA',
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
    }}>
      <AppBar position="static" sx={{ 
        bgcolor: '#3D263B',
        boxShadow: '0 2px 8px rgba(61, 38, 59, 0.15)'
      }}>
        <Toolbar sx={{ py: 3, justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Box 
            onClick={() => window.location.reload()} 
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              ml: { xs: 1, sm: 0 }, // Add left margin on mobile
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Box sx={{ 
              width: { xs: '112px', sm: '225px' },
              height: { xs: '28px', sm: '56px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start' // Align to left instead of center
            }}>
              <ZoneAILogo 
                size={225} 
                color="#ffffff" 
              />
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'white', 
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' } // Hide on mobile
              }}
            >
              Welcome, {user?.name}
            </Typography>
            <IconButton onClick={onLogout} sx={{ color: 'white' }}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
        {/* Gender Tabs */}
        <Box sx={{ bgcolor: '#2A1A27', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Tabs 
            value={gender} 
            onChange={handleGenderChange}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ 
              minHeight: 48,
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#fff'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#E65E33',
                height: 3
              }
            }}
          >
            <Tab label="Men's Basketball" value="men" />
            <Tab label="Women's Basketball" value="women" />
          </Tabs>
        </Box>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Search Section */}
        <Paper sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(61, 38, 59, 0.08)'
        }}>
          <Box position="relative">
            <TextField
              fullWidth
              placeholder="Search for a player..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  py: 1
                }
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#E65E33', mr: 1 }} />,
                endAdornment: searchQuery && (
                  <IconButton
                    onClick={() => handleSearch('')}
                    size="small"
                    sx={{ 
                      color: '#6B7280',
                      '&:hover': { color: '#E65E33' }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
            
            {showSuggestions && suggestions.length > 0 && (
              <Paper sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                border: '1px solid rgba(61, 38, 59, 0.08)'
              }}>
                <List>
                  {suggestions.map((player, index) => (
                    <ListItem
                      key={player.id}
                      onClick={() => handleSelectPlayer(player)}
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: index === selectedSuggestionIndex ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemText
                        primary={player.name}
                        secondary={`${player.position} • ${player.school}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 2,
              fontSize: '0.8rem',
              lineHeight: 1.4,
              color: '#6B7280'
            }}
          >
            Enter the name of <strong>any</strong> D1 college basketball player and ZoneAI returns the <em>most similar players</em> via advanced AI Algorithms
          </Typography>
        </Paper>

        {/* Selected Player */}
        {selectedPlayer && (
          <Paper sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(61, 38, 59, 0.08)'
          }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: '#3D263B',
                mb: 3
              }}
            >
              Selected Player
            </Typography>
            
            <Box display="flex" alignItems="center" gap={3} mb={3}>
              <Avatar sx={{ 
                bgcolor: '#E65E33',
                width: 80,
                height: 80,
                boxShadow: '0 4px 12px rgba(230, 94, 51, 0.3)'
              }}>
                <ZoneAILogo size={36} color="#ffffff" />
              </Avatar>
              
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3D263B' }}>
                  {selectedPlayer.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {selectedPlayer.position} • {selectedPlayer.school} • 2024-25 • {getClassDisplayName(selectedPlayer.class)}
                </Typography>
              </Box>
            </Box>

            {/* Stat Cards */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {selectedPlayer.stats.pointsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  PPG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {selectedPlayer.stats.reboundsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  RPG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {selectedPlayer.stats.assistsPerGame.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  APG
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {(selectedPlayer.stats.effectiveFieldGoalPercentage * 100).toFixed(1)}%
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  eFG%
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {selectedPlayer.stats.offensiveRating.toFixed(0)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Off Rating
                </Typography>
              </Box>

              <Box sx={{ 
                minWidth: '90px', 
                textAlign: 'center',
                p: 2,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#3D263B',
                    fontSize: '1.25rem'
                  }}
                >
                  {selectedPlayer.stats.defensiveRating.toFixed(0)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#6B7280',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Def Rating
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Similar Players */}
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: '#E65E33' }} />
          </Box>
        )}

        {!loading && similarPlayers.length > 0 && (
          <Box>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                fontWeight: 600,
                color: '#3D263B',
                mb: 3
              }}
            >
              Similar Players ({similarPlayers.length})
            </Typography>
            {similarPlayers.map(renderPlayerCard)}
          </Box>
        )}

        {!loading && selectedPlayer && similarPlayers.length === 0 && (
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(61, 38, 59, 0.08)'
          }}>
            <BasketballIcon sx={{ 
              color: '#E65E33', 
              fontSize: '4rem', 
              mb: 2 
            }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#6B7280',
                fontWeight: 500
              }}
            >
              No similar players found
            </Typography>
          </Paper>
        )}

        {/* ZoneAI Bottom Component - Always visible */}
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(61, 38, 59, 0.08)',
          mt: 4
        }}>
          <Box sx={{ 
            width: { xs: '125px', sm: '250px' },
            height: { xs: '31px', sm: '62px' },
            mx: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <ZoneAILogo size={250} color="#E65E33" />
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#3D263B',
              fontWeight: 600,
              mt: 4, // Increased from 3 to 4 for more padding
              mb: 2
            }}
          >
            Navigate the Transfer Portal with Ease
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#6B7280',
              fontSize: '1.1rem',
              maxWidth: '500px',
              mx: 'auto'
            }}
          >
            Leverage ZoneAI's advanced algorithms to find the best players to target in the transfer portal and save on NIL costs
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};
